import React, { useEffect, useRef, useState } from 'react';
import { Modal, Descriptions, Tag, InputNumber, Button, Typography, Space, Row, Col, Switch, Input, Tooltip, message } from 'antd';
import { ShopOutlined, WarningFilled } from '@ant-design/icons';
import type { Shipment } from '../../../services/logistics';
import { formatMoney } from '../shipping-rates/units';
import { adjustShipmentFee } from '../../../services/finance';

const { Text } = Typography;

const RECORD_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  voided: 'red',
};

// 调整方向未选中时，特意做成"看起来像禁用按钮"的样式（而不是真的 disabled）——
// 未选中的那个选项本身还是要能点击切换过去的，只是视觉上弱化，让选中的那个更显眼。
const UNSELECTED_DIRECTION_STYLE: React.CSSProperties = {
  color: 'rgba(0, 0, 0, 0.25)',
  backgroundColor: '#f5f5f5',
  borderColor: '#d9d9d9',
};

interface FeeAdjustmentModalProps {
  shipment: Shipment | null;
  onClose: () => void;
  /** 调整成功后回调——由父组件负责刷新列表，这个弹窗自己不管刷新逻辑 */
  onSuccess?: () => void;
}

const FeeAdjustmentModal: React.FC<FeeAdjustmentModalProps> = ({ shipment, onClose, onSuccess }) => {
  const [direction, setDirection] = useState<'increase' | 'decrease'>('decrease');
  const [amount, setAmount] = useState<number | null>(null);
  const [fullRefund, setFullRefund] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 幂等键在弹窗打开的这一刻生成一次、后续重复提交/失败重试都复用同一个，不能每次点
  // "确认调整"都重新生成——不然误触双击或者网络重试会导致后端把它当成两次不同的调整，
  // 真的扣两次款。
  const idempotencyKeyRef = useRef('');

  // 每次换一条运单（或重新打开），清空上一次的输入，不带着上一条记录的调整草稿
  useEffect(() => {
    setDirection('decrease');
    setAmount(null);
    setFullRefund(false);
    setReason('承运商账单调整');
    setSubmitting(false);
    if (shipment) {
      idempotencyKeyRef.current = `shipment-adj-${shipment.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
  }, [shipment?.id]);

  const quoted = shipment ? Number(shipment.quoted_shipping_fee) : 0;
  const deposit = shipment ? Number(shipment.deposit_frozen) : 0;
  const currentFinal = shipment ? Number(shipment.final_shipping_fee) : 0;
  const purchaseTotal = quoted + deposit;
  const amt = amount || 0;
  // 在当前最终运费的基础上叠加这次调整，不是从预估运费重新算——如果之前已经调整过一次，
  // 这次调整应该接着上次的结果继续算，跟后端 apply_shipment_adjustment 的语义保持一致。
  const adjustedFinal = direction === 'increase' ? currentFinal + amt : currentFinal - amt;
  const gatewayCost = shipment?.gateway_cost_price != null ? Number(shipment.gateway_cost_price) : null;
  const belowGatewayCost = gatewayCost != null && adjustedFinal < gatewayCost;

  // 全额退还运费开关：打开时调整方向锁定为"减少"，金额默认是预估运费本身——不是
  // "已支付总额"（已支付总额里还含保证金，保证金不是这个开关要退的对象）。
  const handleFullRefundToggle = (checked: boolean) => {
    setFullRefund(checked);
    if (checked) {
      setDirection('decrease');
      setAmount(quoted);
    }
  };

  // 开关打开之后，只要用户又手动改了方向或金额，说明已经不是"照开关填的那个值"了，
  // 把开关关掉，避免界面上显示"已开启"却和当前方向/金额对不上。
  const handleDirectionChange = (value: 'increase' | 'decrease') => {
    setDirection(value);
    setFullRefund(false);
  };

  const handleAmountChange = (value: number | null) => {
    setAmount(value);
    setFullRefund(false);
  };

  const handleConfirm = async () => {
    if (!shipment || !amount || amount <= 0 || !reason.trim()) return;
    const signedAmount = direction === 'increase' ? amount : -amount;
    setSubmitting(true);
    try {
      await adjustShipmentFee({
        shipment_id: shipment.id,
        delta_amount: signedAmount,
        remark: reason.trim(),
        idempotency_key: idempotencyKeyRef.current,
      });
      message.success(`调整成功，最终运费已更新为 ${formatMoney(adjustedFinal, shipment.currency)}`);
      onClose();
      onSuccess?.();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '调整失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={shipment ? (
        <Space>
          <div style={{ width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {shipment.carrier_logo ? (
              <img src={shipment.carrier_logo} alt={shipment.carrier_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ShopOutlined style={{ color: '#bfbfbf', fontSize: 16 }} />
            )}
          </div>
          <span>{shipment.carrier_name}{shipment.service_name ? ` - ${shipment.service_name}` : ''}</span>
          <Text type="secondary" style={{ fontWeight: 'normal' }}>{shipment.tracking_number}</Text>
        </Space>
      ) : '运费调整'}
      open={!!shipment}
      onCancel={submitting ? undefined : onClose}
      maskClosable={!submitting}
      closable={!submitting}
      width={600}
      destroyOnClose
      footer={shipment ? [
        <Button key="cancel" disabled={submitting} onClick={onClose}>取消</Button>,
        <Button
          key="confirm"
          type="primary"
          loading={submitting}
          disabled={!amount || amount <= 0 || !reason.trim()}
          onClick={handleConfirm}
        >
          确认调整
        </Button>,
      ] : null}
    >
      {shipment && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="网关">{shipment.gateway_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="网关成本价">
              {shipment.gateway_cost_price == null ? '-' : formatMoney(shipment.gateway_cost_price, shipment.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="预估运费">{formatMoney(shipment.quoted_shipping_fee, shipment.currency)}</Descriptions.Item>
            <Descriptions.Item label="保证金">{formatMoney(shipment.deposit_frozen, shipment.currency)}</Descriptions.Item>
            <Descriptions.Item label="已支付总额">{formatMoney(purchaseTotal, shipment.currency)}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={RECORD_STATUS_COLORS[shipment.record_status] || 'default'}>
                {shipment.record_status_display}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <div style={{ marginBottom: 16 }}>
            <Space align="center">
              <Switch checked={fullRefund} onChange={handleFullRefundToggle} />
              <Text>全额退还运费</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                常用于已确认作废的运单退费，将全额退还已支付运费
              </Text>
            </Space>
          </div>

          <Row gutter={24} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>运费调整方向</div>
              <Space>
                <Button
                  type={direction === 'increase' ? 'primary' : 'default'}
                  style={direction === 'increase' ? undefined : UNSELECTED_DIRECTION_STYLE}
                  onClick={() => handleDirectionChange('increase')}
                >
                  增加运费
                </Button>
                <Button
                  type={direction === 'decrease' ? 'primary' : 'default'}
                  style={direction === 'decrease' ? undefined : UNSELECTED_DIRECTION_STYLE}
                  onClick={() => handleDirectionChange('decrease')}
                >
                  减少运费
                </Button>
              </Space>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>调整金额</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                addonBefore={shipment.currency}
                value={amount ?? undefined}
                onChange={(v) => handleAmountChange(typeof v === 'number' ? v : null)}
              />
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>调整原因</div>
            <Input.TextArea
              rows={2}
              placeholder="请填写此次调整的原因，如客户实际入仓超重/超尺、承运商账单调整、运单作废退费等"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div style={{ background: '#f0f5ff', borderRadius: 8, padding: '10px 20px' }}>
            <div>
              当前最终运费 {formatMoney(shipment.final_shipping_fee, shipment.currency)}
              {amt > 0 && (
                <Text style={{ color: direction === 'increase' ? '#f5222d' : '#52c41a', marginLeft: 8 }}>
                  {direction === 'increase' ? '+' : '-'}{amt.toFixed(2)}
                </Text>
              )}
            </div>
            <div style={{ marginTop: 4 }}>
              <Text strong style={{ fontSize: 16 }}>
                调整后运费 {formatMoney(adjustedFinal, shipment.currency)}
              </Text>
              {belowGatewayCost && (
                <Tooltip title={`调整后运费低于网关成本价（${formatMoney(gatewayCost as number, shipment.currency)}），这笔运单将出现亏损`}>
                  <Text style={{ color: '#f5222d', marginLeft: 8 }}>
                    <WarningFilled style={{ marginRight: 4 }} />
                    已低于网关成本价
                  </Text>
                </Tooltip>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default FeeAdjustmentModal;
